'use client';

import _ from 'lodash';
import { classNames } from 'primereact/utils';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import style from './search-bar.module.css';

interface Props<T> {
	suggestions: T[];
	placeholder?: string;
	onSelect: (item: T) => void;
	onChange: (item: string) => void;
	filterMethod: (item: T, query: string) => boolean;
	label: keyof T;
	labelIcon?: ReactNode;
	description?: keyof T | null;
	value?: string;
	className?: string;
	inputClassName?: string;
	iconClassName?: string;
	forceSelection?: boolean;
}

const AutoCompleteSearch = <T,>(props: Props<T>) => {
	const {
		suggestions,
		placeholder = 'Search',
		onSelect,
		onChange,
		filterMethod,
		label,
		description,
		value = '',
		className,
		inputClassName,
		iconClassName,
		labelIcon,
	} = props;

	const [filteredResults, setFilteredResults] = useState<T[]>([]);
	const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
	const searchRef = useRef<HTMLDivElement>(null);

	// Filter function for search
	useEffect(() => {
		if (!value) {
			setFilteredResults([]);
		} else {
			const filtered = suggestions.filter((item) =>
				filterMethod(item, value),
			);
			setFilteredResults(_.uniqBy(filtered, label));
		}
	}, [value, suggestions, filterMethod, label]);

	// Handle selecting a suggestion
	const handleSelect = (result: T) => {
		onChange(String(result[label])); // Display selected item
		setShowSuggestions(false);
		onSelect(result); // Call external function
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				searchRef.current &&
				!searchRef.current.contains(event.target as Node)
			) {
				setShowSuggestions(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<div className="max-w-full w-full relative" ref={searchRef}>
			{/* Search Input Box */}
			<div
				className={classNames(
					{
						'rounded-t-3xl': showSuggestions,
						'rounded-3xl': !showSuggestions,
					},
					style.autocompleteSearchBar,
					className,
				)}
			>
				<div className="flex items-center">
					<MdSearch
						size={24}
						className={classNames(
							'text-onSurfaceVariant mr-2',
							iconClassName,
						)}
					/>
					<input
						type="text"
						value={value}
						onChange={(e) => {
							onChange(e.target.value);
							setShowSuggestions(true);
						}}
						placeholder={placeholder}
						className={classNames(
							style.autocompleteSearchBarInput,
							inputClassName,
						)}
					/>
				</div>
			</div>

			{/* Search Suggestions Dropdown */}
			{showSuggestions && (
				<div
					className={style.autocompleteSearchBarResult}
					style={{ top: '100%' }} // Ensures dropdown floats above other elements
				>
					{filteredResults.length > 0 ? (
						filteredResults.map((result, index) => (
							<div
								key={`${String(result[label])}-${index}`}
								onClick={() => handleSelect(result)}
								onKeyDown={(e) => {
									// Check for Enter or Space key press to trigger the click action
									if (e.key === 'Enter' || e.key === ' ') {
										handleSelect(result);
									}
								}}
								className={
									style.autocompleteSearchBarResultItem
								}
							>
								{labelIcon ? (
									<div className="flex items-center text-body-large text-onSurface">
										{labelIcon} {String(result[label])}
									</div>
								) : (
									<p className="text-body-large text-onSurface">
										{String(result[label])}
									</p>
								)}
								{description && (
									<p className="text-onSurfaceVariant text-body-medium">
										{String(result[description])}
									</p>
								)}
							</div>
						))
					) : (
						<div className="text-body-large text-onSurface px-4 py-2">
							No results found.
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AutoCompleteSearch;
